import React, { useState, useEffect, useMemo } from "react";
import { Search, MapPin, Users, Calendar, ArrowRight, Star, Target, Zap, Trophy, Clock, Award, Route, RefreshCw, Filter } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import Navigation from "./Navigation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useFetch } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { transformMeetings, filterAndSortMeetings, transformMeetingToRunCard, matchesTimeFilter } from "../utils/dataTransformers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import RunRegistrationForm from "./RunRegistrationForm";
import MeetingCard from "./ui/MeetingCard";
import ScrollableCardModal from "./ui/ScrollableCardModal";
import FilterModal from "./ui/FilterModal";
import "../styles/mobile.css";

const RUN_PAGE_MEETINGS_LIMIT = 50;

const RunPage = () => {
  const [searchCity, setSearchCity] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("This Week");
  const [currentPage, setCurrentPage] = useState(1);
  const [runEvents, setRunEvents] = useState([]);
  // loadingEvents is now provided by useFetch hook
  const [selectedRun, setSelectedRun] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();

  // URL → state. We read cityParam here too so the fetch below can re-trigger
  // when the city in the URL changes (e.g. hero search → /run?city=Miami).
  const cityParam = (searchParams.get('city') || '').trim();

  // Handle URL parameters on component mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    const timeParam = searchParams.get('time');

    if (filterParam) {
      setSelectedFilters([filterParam]);
    }

    if (timeParam) {
      // Capitalize first letter to match timeFilters format
      const capitalizedTime = timeParam.charAt(0).toUpperCase() + timeParam.slice(1);
      if (timeFilters.includes(capitalizedTime)) {
        setSelectedTimeFilter(capitalizedTime);
      }
    }

    if (cityParam) {
      setSearchCity(cityParam);
    }
  }, [searchParams, cityParam]);


  // Fetch events. When the URL has ?city=X, pass it to the backend so we
  // filter server-side (the prior client-side-only approach missed matches
  // beyond the first 50 returned). No user_lat/user_lon → no proximity-based
  // distance filter applied; the backend's `within_miles` is opt-in and we
  // never set it from the hero search.
  const { data: meetings, loading: loadingEvents, refetch: refetchEvents } = useFetch(
    () => apiService.meetings.getAll({
      limit: RUN_PAGE_MEETINGS_LIMIT,
      ...(cityParam ? { city: cityParam } : {}),
    }),
    [cityParam],
    { enabled: true }
  );

  // Transform meetings data using same logic as dashboard
  const transformedMeetings = useMemo(() => {
    if (!meetings) return [];
    return transformMeetings(meetings);
  }, [meetings]);

  // Filter upcoming events using same utility as dashboard
  const upcomingEvents = useMemo(() => {
    const filtered = filterAndSortMeetings(transformedMeetings, {
      status: 'upcoming',
      sortBy: 'scheduled_date',
      sortOrder: 'asc',
      limit: RUN_PAGE_MEETINGS_LIMIT
    });
    
    return filtered;
  }, [transformedMeetings]);

  // Transform to the format expected by the UI and apply time filter
  useEffect(() => {
    if (upcomingEvents && upcomingEvents.length > 0) {
      let filtered = upcomingEvents;
      
      // Apply time filter before transforming
      if (selectedTimeFilter !== 'All') {
        filtered = upcomingEvents.filter(meeting => {
          if (!meeting.scheduled_date) return false;
          return matchesTimeFilter(meeting.scheduled_date, selectedTimeFilter);
        });
      }
      
      const transformedEvents = filtered.map((meeting) => {
        return transformMeetingToRunCard(meeting);
      });
      
      setRunEvents(transformedEvents);
    } else {
      setRunEvents([]);
    }
  }, [upcomingEvents, selectedTimeFilter]);

  // Fetch events on component mount
  // Events are now fetched automatically by useFetch hook

  const meetingFilters = ["Social", "Tempo", "Long Run", "Intervals"];
  const timeFilters = ["All", "Today", "Tomorrow", "This Week", "Next Week"];

  // Popular cities for autocomplete
  const POPULAR_CITIES = [
    'New York, NY',
    'Los Angeles, CA',
    'Chicago, IL',
    'Houston, TX',
    'Phoenix, AZ',
    'Philadelphia, PA',
    'San Antonio, TX',
    'San Diego, CA',
    'Dallas, TX',
    'San Jose, CA',
    'Austin, TX',
    'Jacksonville, FL',
    'Fort Worth, TX',
    'Columbus, OH',
    'Charlotte, NC',
    'San Francisco, CA',
    'Indianapolis, IN',
    'Seattle, WA',
    'Denver, CO',
    'Washington, DC',
    'Boston, MA',
    'El Paso, TX',
    'Nashville, TN',
    'Detroit, MI',
    'Oklahoma City, OK',
    'Portland, OR',
    'Las Vegas, NV',
    'Memphis, TN',
    'Louisville, KY',
    'Baltimore, MD',
    'Milwaukee, WI',
    'Albuquerque, NM',
    'Tucson, AZ',
    'Fresno, CA',
    'Sacramento, CA',
    'Mesa, AZ',
    'Kansas City, MO',
    'Atlanta, GA',
    'Long Beach, CA',
    'Colorado Springs, CO',
    'Raleigh, NC',
    'Miami, FL',
    'Virginia Beach, VA',
    'Omaha, NE',
    'Oakland, CA',
    'Minneapolis, MN',
    'Tulsa, OK',
    'Arlington, TX',
    'Tampa, FL',
    'New Orleans, LA'
  ];

  // Filter events based on selected filters
  const filteredEvents = runEvents.filter(event => {
    // City filter - improved to handle null/undefined data and better matching
    if (searchCity.trim()) {
      const searchCityLower = searchCity.toLowerCase().trim();
      
      // Get the event's city data - handle both combined format and separate fields
      let eventCityLower = '';
      if (event.city) {
        eventCityLower = event.city.toLowerCase();
      }
      
      // Also check individual city and state fields if they exist
      const eventCity = event.cityOnly || '';
      const eventState = event.stateOnly || '';
      const eventCityOnly = eventCity.toLowerCase();
      const eventStateOnly = eventState.toLowerCase();
      
      // Check if search term matches any part of the city data
      const matchesCombined = eventCityLower.includes(searchCityLower);
      const matchesCityOnly = eventCityOnly.includes(searchCityLower);
      const matchesStateOnly = eventStateOnly.includes(searchCityLower);
      const matchesReverse = searchCityLower.includes(eventCityOnly) || searchCityLower.includes(eventStateOnly);
      
      if (!matchesCombined && !matchesCityOnly && !matchesStateOnly && !matchesReverse) {
        return false;
      }
    }

    // Time filter is now applied before transformation in useEffect

    // Type filter - improved matching
    if (selectedFilters.length > 0) {
      return selectedFilters.some(filter => {
        const filterLower = filter.toLowerCase();
        const eventTypeLower = event.type.toLowerCase();
        const eventDistanceLower = event.distance.toLowerCase();
        
        // Direct type matching
        if (eventTypeLower.includes(filterLower) || filterLower.includes(eventTypeLower)) {
          return true;
        }
        
        // Distance matching
        if (eventDistanceLower.includes(filterLower) || filterLower.includes(eventDistanceLower)) {
          return true;
        }
        
        // Special cases for better matching
        if (filterLower === "long run" && eventDistanceLower.includes("10mi")) return true;
        if (filterLower === "tempo" && (eventTypeLower.includes("workout") || eventTypeLower.includes("training-focused"))) return true;
        if (filterLower === "intervals" && (eventTypeLower.includes("workout") || eventTypeLower.includes("training-focused"))) return true;
        
        return false;
      });
    }

    return true;
  });

  // Pagination logic
  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, selectedTimeFilter, searchCity]);

  const handleSearch = () => {
    // Search functionality is handled by the filteredEvents logic
    // The search input change already triggers filtering
    // This function can be used for additional search logic if needed
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Generate suggestions based on search query
  const generateSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    
    return POPULAR_CITIES.filter(city => 
      city.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5); // Limit to 5 suggestions
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchCity(value);
    
    if (value.length >= 2) {
      const newSuggestions = generateSuggestions(value);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (city) => {
    setSearchCity(city);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchCity.length >= 2) {
      const newSuggestions = generateSuggestions(searchCity);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }
  };

  // Handle input blur (with delay to allow clicking on suggestions)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const toggleFilter = (filter) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setSelectedTimeFilter("All");
    setSearchCity("");
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary pwa-content">
      <Navigation />
      
      {/* Mobile Filter Button - Fixed to top right */}
      <div className="mobile-filter-btn fixed top-4 right-4 z-50">
        <Button
          variant="runcards-secondary"
          onClick={() => setShowFilterModal(true)}
          className="rounded-runcards-round p-3"
        >
          <Filter className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden bg-runcards-background">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Marcus_Speed_and_Form.png')",
            filter: "grayscale(100%) brightness(0.6)"
          }}
        />
        <div className="absolute inset-0 bg-runcards-background/50" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto pt-24 sm:pt-80 lg:pt-96">

          {/* Main Title */}
          <div className="mb-6">
            <h2 className="text-runcards-title sm:text-4xl md:text-5xl lg:text-6xl font-runcards-bold mb-4 text-runcards-text-primary w-full" style={{letterSpacing: '-0.02em', transform: 'scaleX(1.5)', whiteSpace: 'nowrap'}}>
              FIND YOUR RUN
            </h2>
            <p className="text-runcards-xl sm:text-runcards-xxl font-runcards text-runcards-text-tertiary">
              Discover upcoming runs in your area.
            </p>
          </div>
          
          {/* Search Bar - Hidden on mobile, visible on desktop */}
          <div className="desktop-filters hidden sm:block max-w-2xl mx-auto mb-8">
            <div className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-runcards-text-secondary w-5 h-5 z-10" />
                  <Input
                    type="text"
                    variant="runcards"
                    placeholder="Search by city..."
                    value={searchCity}
                    onChange={handleSearchChange}
                    onKeyPress={handleKeyPress}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="pl-12 pr-4 h-14 text-runcards-lg font-runcards rounded-l-runcards-lg rounded-r-none border-r-0"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-runcards-foreground backdrop-blur-xl border border-runcards-border rounded-b-runcards-lg mt-1 z-50">
                      {suggestions.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionSelect(city)}
                          className="w-full text-left px-4 py-3 text-runcards-text-primary hover:bg-runcards-surface transition-colors font-runcards"
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="runcards-secondary"
                  size="runcards-large"
                  onClick={handleSearch}
                  className="rounded-r-runcards-lg rounded-l-none h-14 px-6 border-l-0"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filter Buttons - Hidden on mobile, visible on desktop */}
          <div className="desktop-filters hidden sm:flex flex-wrap justify-center gap-3 mb-6">
            {meetingFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => toggleFilter(filter)}
                className={`px-4 py-2 rounded-runcards-round text-runcards-sm font-runcards transition-all duration-200 ${
                  selectedFilters.includes(filter)
                    ? 'bg-runcards-primary text-runcards-text-on-light'
                    : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground border border-runcards-border'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Runs Section */}
      <section className="py-16 px-4 sm:px-6 bg-runcards-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-12">
            <div className="text-center sm:text-left">
              <h2 className="text-runcards-xxl sm:text-runcards-title md:text-3xl lg:text-4xl font-runcards-bold text-runcards-text-primary mb-2 sm:mb-4">
                {searchCity.trim() ? `Runs in ${searchCity}` : 'Upcoming Runs'}
              </h2>
              <p className="text-runcards-text-secondary text-runcards-lg font-runcards">
                <span className="text-white" style={{ fontSize: '1.2em' }}>{filteredEvents.length} {filteredEvents.length === 1 ? 'run' : 'runs'}</span>
                <span> found</span>
                {searchCity.trim() && ` in ${searchCity}`}
                {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
              </p>
            </div>
            <Select value={selectedTimeFilter} onValueChange={setSelectedTimeFilter}>
              <SelectTrigger className="w-[140px] bg-runcards-foreground border-runcards-border text-runcards-text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeFilters.map((filter) => (
                  <SelectItem key={filter} value={filter}>
                    {filter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {loadingEvents ? (
            <div className="text-center py-16">
              <div className="bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border rounded-runcards-lg p-8 max-w-md mx-auto">
                <RefreshCw className="w-16 h-16 text-runcards-text-primary mx-auto mb-4 animate-spin" />
                <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2">Loading runs...</h3>
                <p className="text-runcards-text-secondary font-runcards">Fetching the latest events for you.</p>
              </div>
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {paginatedEvents.map((event) => (
                  <MeetingCard
                    key={event.id}
                    event={event}
                    onButtonClick={() => {
                      if (!user) {
                        navigate('/auth');
                      } else if (user.user_type === "runclub") {
                        toast.error("Run club owners cannot register for runs. You can only create and manage runs for your club.");
                      } else {
                        setSelectedRun(event);
                        setShowRunModal(true);
                      }
                    }}
                    buttonText="View Run"
                  />
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-12 space-x-4">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="text-runcards-text-primary hover:text-runcards-text-secondary disabled:opacity-50 disabled:cursor-not-allowed text-runcards-lg font-runcards"
                  >
                    Previous
                  </button>
                  
                  <div className="flex space-x-4">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`text-lg font-medium ${
                          currentPage === page
                            ? 'text-runcards-text-primary font-runcards-bold'
                            : 'text-runcards-text-secondary hover:text-runcards-text-primary font-runcards'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="text-runcards-text-primary hover:text-runcards-text-secondary disabled:opacity-50 disabled:cursor-not-allowed text-runcards-lg font-runcards"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border rounded-runcards-lg p-8 max-w-md mx-auto">
                <Filter className="w-16 h-16 text-runcards-text-secondary mx-auto mb-4" />
                <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2">
                  {searchCity.trim() ? `No runs found in ${searchCity}` : 'No runs found'}
                </h3>
                <p className="text-runcards-text-secondary mb-6 font-runcards">
                  {searchCity.trim() 
                    ? `Try searching for a different city or adjusting your filters to find more runs.`
                    : 'Try adjusting your filters to find more runs.'
                  }
                </p>
                <Button 
                  variant="runcards-secondary"
                  size="runcards-medium"
                  onClick={() => {
                    setSelectedFilters([]);
                    setSelectedTimeFilter("All");
                    setSearchCity("");
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Registration Form Modal */}
      {showRegistrationForm && selectedRun && (
        <RunRegistrationForm
          meeting={selectedRun}
          onClose={() => {
            setShowRegistrationForm(false);
            setSelectedRun(null);
          }}
          onSuccess={() => {
            // Optionally refresh data or show success message
            setShowRegistrationForm(false);
            setSelectedRun(null);
          }}
        />
      )}

      {/* Meeting Modal */}
      {selectedRun && (
        <ScrollableCardModal
          cardType="meeting"
          cardId={selectedRun.id}
          isOpen={showRunModal}
          onClose={() => {
            setShowRunModal(false);
            setSelectedRun(null);
          }}
        />
      )}

      {/* Filter Modal */}
      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        searchCity={searchCity}
        onSearchCityChange={handleSearchChange}
        onSearchCityFocus={handleInputFocus}
        onSearchCityBlur={handleInputBlur}
        onKeyPress={handleKeyPress}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        onSuggestionSelect={handleSuggestionSelect}
        selectedFilters={selectedFilters}
        onToggleFilter={toggleFilter}
        selectedTimeFilter={selectedTimeFilter}
        onTimeFilterChange={setSelectedTimeFilter}
        runFilters={meetingFilters}
        timeFilters={timeFilters}
        onClearFilters={clearAllFilters}
      />
    </div>
  );
};

export default RunPage;
